<template>
    <form class="mt-6 flex max-w-3xl flex-col gap-6" @submit.prevent="submit">
        <TextInput
            v-model="form.name"
            :label="t('vue.name')"
            type="text"
            :error="errors.name"
        />

        <TextInput
            v-model="form.slug"
            :label="t('vue.ai_skills_slug')"
            type="text"
            :placeholder="t('vue.ai_skills_slug_hint')"
            :error="errors.slug"
        />

        <TextInput
            v-model="form.description"
            :label="t('vue.description')"
            type="text"
            :error="errors.description"
        />

        <TextAreaInput
            v-model="form.prompt"
            :label="t('vue.ai_skills_prompt')"
            :rows="12"
            :error="errors.prompt"
        />

        <div>
            <p class="mb-2 text-sm font-medium text-gray-700">
                {{ t("vue.ai_skills_recipe") }}
            </p>
            <p class="mb-3 text-xs text-gray-400">
                {{ t("vue.ai_skills_recipe_hint") }}
            </p>

            <div
                v-for="(step, index) in form.tool_recipe"
                :key="index"
                class="mb-3 rounded-xl border border-gray-200 p-4"
            >
                <div class="flex items-start gap-3">
                    <SelectInput
                        v-model="step.key"
                        class="w-64"
                        :data="toolOptions"
                        :label="t('vue.ai_skills_tool')"
                        :placeholder="t('vue.ai_skills_tool')"
                    />
                    <button
                        type="button"
                        class="mt-8 text-gray-400 transition hover:text-red-500"
                        @click="removeStep(index)"
                    >
                        <TrashIcon class="h-5 w-5" />
                    </button>
                </div>
                <TextAreaInput
                    v-model="paramsText[index]"
                    class="mt-3"
                    :label="t('vue.ai_skills_params')"
                    :rows="2"
                    :placeholder="'{ &quot;days&quot;: 30 }'"
                    :error="paramsError[index]"
                />
            </div>

            <AtomicButton
                type="button"
                size="sm"
                color="white"
                @click="addStep"
            >
                {{ t("vue.ai_skills_add_tool") }}
            </AtomicButton>
        </div>

        <div class="grid gap-6 sm:grid-cols-3">
            <SelectInput
                v-model="form.model"
                :data="modelOptions"
                :label="t('vue.ai_skills_model')"
                :placeholder="t('vue.ai_skills_model_default')"
            />
            <TextInput
                v-model.number="form.temperature"
                :label="t('vue.ai_skills_temperature')"
                type="number"
                step="0.1"
                :error="errors.temperature"
            />
            <TextInput
                v-model.number="form.max_output_tokens"
                :label="t('vue.ai_skills_max_tokens')"
                type="number"
                :error="errors.max_output_tokens"
            />
        </div>

        <CheckboxInput
            v-model:checked="form.is_active"
            :label="t('vue.ai_skills_active')"
        />

        <div>
            <AtomicButton
                type="submit"
                color="yellow"
                size="sm"
                :disabled="loading"
            >
                {{ t("vue.save") }}
            </AtomicButton>
        </div>
    </form>
</template>

<script lang="ts" setup>
import { reactive, ref } from "vue";
import { router } from "@inertiajs/vue3";
import { TrashIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type {
    IInternalAiSkillFormData,
    IInternalAiSkillToolOption,
} from "@/types/AiChat/IInternalAiSkill";

const { t } = useTranslations();

const props = defineProps<{
    skill: IInternalAiSkillFormData;
    tools: IInternalAiSkillToolOption[];
    models: string[];
    submitUrl: string;
    method: "post" | "put";
}>();

const form = reactive<IInternalAiSkillFormData>({
    id: props.skill.id,
    slug: props.skill.slug,
    name: props.skill.name,
    description: props.skill.description,
    prompt: props.skill.prompt,
    tool_recipe: props.skill.tool_recipe.map((step) => ({ ...step })),
    model: props.skill.model,
    temperature: props.skill.temperature,
    max_output_tokens: props.skill.max_output_tokens,
    is_active: props.skill.is_active,
});

const toolOptions = props.tools.map((tool) => ({
    title: tool.key,
    value: tool.key,
}));

const modelOptions = props.models.map((model) => ({
    title: model,
    value: model,
}));

// The params object is edited as JSON text per row, parsed on submit.
const paramsText = reactive<string[]>(
    form.tool_recipe.map((step) =>
        Object.keys(step.params).length ? JSON.stringify(step.params) : "",
    ),
);
const paramsError = reactive<string[]>(form.tool_recipe.map(() => ""));

const errors = ref<IInputErrors>({});
const loading = ref(false);

const addStep = () => {
    form.tool_recipe.push({ key: toolOptions[0]?.value ?? "", params: {} });
    paramsText.push("");
    paramsError.push("");
};

const removeStep = (index: number) => {
    form.tool_recipe.splice(index, 1);
    paramsText.splice(index, 1);
    paramsError.splice(index, 1);
};

const buildRecipe = (): boolean => {
    let valid = true;

    form.tool_recipe.forEach((step, index) => {
        paramsError[index] = "";
        const text = paramsText[index]?.trim() ?? "";

        if (text === "") {
            step.params = {};
            return;
        }

        try {
            const parsed = JSON.parse(text);
            if (typeof parsed === "object" && parsed !== null) {
                step.params = parsed as Record<string, unknown>;
            } else {
                paramsError[index] = t("vue.ai_skills_params_invalid");
                valid = false;
            }
        } catch {
            paramsError[index] = t("vue.ai_skills_params_invalid");
            valid = false;
        }
    });

    return valid;
};

const submit = () => {
    if (!buildRecipe()) {
        return;
    }

    loading.value = true;
    const payload = { ...form, _method: props.method };

    router.post(props.submitUrl, payload, {
        preserveScroll: true,
        onError: (err) => {
            errors.value = err;
        },
        onSuccess: () => {
            errors.value = {};
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};
</script>
